// src/services/utils.js
import { createEvent } from 'ics'; // WICHTIG: 'ics' Paket muss installiert sein

/**
 * Generiert eine zufällige, eindeutige ID (UUID v4)
 * @returns {string}
 */
const generateId = () => crypto.randomUUID();

/**
 * Ermittelt die Firmen-ID anhand der aktuellen Subdomain
 * @returns {string | null} Die Company ID oder null
 */
const getCompanyIdFromSubdomain = () => {
    const hostname = window.location.hostname;
    // Prüft auf localhost oder IP-Adressen
    if (hostname.includes('localhost') || /^\d/.test(hostname)) {
        return null;
    }
    const parts = hostname.split('.');
    // Wenn die Domain mindestens 3 Teile hat (z.B. test.meetform.de)
    // und der erste Teil nicht 'www' ist
    if (parts.length >= 3 && parts[0] !== 'www') {
        return parts[0];
    }
    return null;
};

/**
 * Indexiert Agendapunkte rekursiv neu (z.B. 1, 1.1, 1.2, 2, 2.1)
 * KORRIGIERT: Stellt sicher, dass `items` und `subPoints` immer Arrays sind, um Laufzeitfehler zu vermeiden.
 * @param {Array<Object>} items 
 * @param {string} parentNumber 
 * @returns {Array<Object>}
 */
const reindexAgendaItems = (items, parentNumber = '') => {
    // Defensiver Check auf das übergebene Array
    if (!Array.isArray(items)) {
        console.warn('reindexAgendaItems called with non-array items:', items);
        return [];
    }

    return items.map((item, index) => {
        const newNumber = parentNumber ? `${parentNumber}.${index + 1}` : `${index + 1}`;
        const updatedItem = { ...item, number: newNumber };
        
        const subPoints = item.subPoints;
        
        // Defensiver Check für subPoints und rekursiver Aufruf
        if (Array.isArray(subPoints) && subPoints.length > 0) {
            updatedItem.subPoints = reindexAgendaItems(subPoints, newNumber);
        } else {
            // Sicherstellen, dass subPoints immer als Array initialisiert ist
            updatedItem.subPoints = []; 
        }
        return updatedItem;
    });
};

/**
 * Prüft, ob der Benutzer Admin oder Teamleiter ist.
 * @param {string | null} userRole 
 * @returns {boolean}
 */
const isAdminOrTeamLeader = (userRole) => {
    return userRole === 'admin' || userRole === 'teamleader';
};

/**
 * Simuliert das Rendern von Markdown (um Build-Fehler zu vermeiden).
 * Gibt den String unverändert zurück.
 * @param {string} markdownText 
 * @returns {string}
 */
const renderMarkdown = (markdownText) => {
    // Hier müsste ein Markdown-Renderer (z.B. marked) verwendet werden, 
    // aber wir geben den Text als String zurück, um den Fehler im Frontend zu beheben.
    return markdownText; 
};

// --- NEU: Funktion zum Erstellen einer iCal Datei ---
/**
 * Lädt eine .ics Datei für das gegebene Meeting herunter.
 * @param {Object} meeting 
 */
const downloadIcsFile = (meeting) => {
    if (!meeting || !meeting.date || !meeting.startTime) return;

    // Datum parsen (YYYY-MM-DD -> [YYYY, MM, DD])
    const dateParts = meeting.date.split('-').map(Number); // [2023, 10, 25]
    const timeParts = meeting.startTime.split(':').map(Number); // [14, 30]
    
    // Dauer berechnen (optional, default 1h)
    let duration = { hours: 1, minutes: 0 };
    if (meeting.endTime) {
        const endParts = meeting.endTime.split(':').map(Number);
        const startMinutes = timeParts[0] * 60 + timeParts[1];
        const endMinutes = endParts[0] * 60 + endParts[1];
        const diff = endMinutes - startMinutes;
        if (diff > 0) {
            duration = { hours: Math.floor(diff / 60), minutes: diff % 60 };
        }
    }

    const event = {
        start: [dateParts[0], dateParts[1], dateParts[2], timeParts[0], timeParts[1]],
        duration: duration,
        title: meeting.title || 'Meeting',
        description: `Link zum Protokoll: https://meetform.de/#/protokoll/${meeting.id}\n\n${meeting.agenda_structure ? 'Agenda liegt vor.' : ''}`,
        location: meeting.location || 'Online',
        status: 'CONFIRMED',
        busyStatus: 'BUSY',
        organizer: { name: meeting.companyName || 'Meetform', email: 'no-reply@meetform.de' },
        url: `https://meetform.de/#/protokoll/${meeting.id}`
    };

    createEvent(event, (error, value) => {
        if (error) {
            console.error("Fehler beim Erstellen der iCal-Datei:", error);
            return;
        }
        const blob = new Blob([value], { type: 'text/calendar;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `meeting-${meeting.id}.ics`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    });
};


export { 
  generateId, 
  getCompanyIdFromSubdomain, 
  reindexAgendaItems,
  isAdminOrTeamLeader,
  renderMarkdown,
  downloadIcsFile // Exportieren
};